"use client";

import { loginWithCodeAction } from "@/app/actions";
import { useSearchParams } from "next/navigation";
import qs from "query-string";
import { useEffect, useRef } from "react";
import { useFormState } from "react-dom";
import Button from "./button";
import FormErrorSummary from "./form/form-error-summary";
import InputField from "./form/input-field";
import RequiredFieldsNote from "./form/required-fields-note";

type OTPLoginProps = {
  email?: string;
  redirectUrl?: string;
  isApp: boolean;
};

export const INVALID_CODE_MESSAGE =
  "Koden du har angivit är felaktig eller utgången. Kontrollera koden eller begär en ny.";

// `attempt` increases on every failed submit. It is passed as errorKey so the
// code field shows the error again even when the text is identical to last
// time, and it triggers the focus move below.
type OTPState = { codeError: string; attempt: number } | null;

const OTPLogin = (props: OTPLoginProps) => {
  const searchParams = useSearchParams();
  const codeRef = useRef<HTMLInputElement>(null);

  const [state, action] = useFormState<OTPState, FormData>(
    async (prev, formData) => {
      const redirectUrl = props.redirectUrl || searchParams.get("redirectUrl");

      const email = formData.get("email")?.toString() || "";
      const code = formData.get("code")?.toString() || "";

      // The code is verified through a server action rather than by
      // navigating to /verify-token: that route redirects to an error page
      // on failure, which throws the whole form away. Here a wrong code
      // becomes an inline error on the field and the user can simply retry.
      const res = await loginWithCodeAction(email, code);

      if (!res.success) {
        return {
          codeError: INVALID_CODE_MESSAGE,
          attempt: (prev?.attempt ?? 0) + 1,
        };
      }

      if (props.isApp) {
        window.location.href = `/login-success?${qs.stringify({
          accessToken: res.auth.access_token,
          refreshToken: res.auth.refresh_token,
          redirectUrl,
        })}`;
      } else if (redirectUrl) {
        window.location.href = decodeURI(redirectUrl);
      } else {
        window.location.href = "/konto/mitt-efn";
      }

      return null;
    },
    null
  );

  // The submit button is disabled while the action is pending, which drops
  // keyboard focus to <body>. After a failed attempt, move focus to the code
  // field: a screen reader then reads the label together with the error
  // (aria-describedby/aria-errormessage), and the wrong code is selected so
  // the user can type the correct one straight away.
  useEffect(() => {
    if (!state?.attempt) return;
    codeRef.current?.focus();
    codeRef.current?.select();
  }, [state?.attempt]);

  return (
    <form className="space-y-4" action={action}>
      <FormErrorSummary hasError={Boolean(state?.codeError)} />
      <RequiredFieldsNote />
      <InputField
        autoFocus={Boolean(!props.email)}
        label="E-postadress"
        type="email"
        name="email"
        placeholder="Ange e-postadress"
        autoComplete="email"
        defaultValue={props.email}
        readOnly={Boolean(props.email)}
        required
      />
      <InputField
        type="text"
        label="Kod"
        required
        inputMode="numeric"
        pattern="\d*"
        minLength={6}
        maxLength={6}
        autoComplete="one-time-code"
        name="code"
        placeholder="Ange kod"
        helpText="Ange kod med 6 siffror."
        autoFocus={Boolean(props.email)}
        inputRef={codeRef}
        error={state?.codeError}
        errorKey={state?.attempt}
      />

      <Button type="submit" showLoader className="w-full btn-large btn-comp">
        Fortsätt
      </Button>
    </form>
  );
};

export default OTPLogin;
